import { Suspense, useState } from "react";
import { safeLazy } from "@/lib/safeLazy";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/contexts/AuthContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";
import { CartProvider } from "@/contexts/CartContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { MobileBottomNav } from "@/components/MobileBottomNav";
import { MainLayout } from "@/components/MainLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { PageTracker } from "@/components/PageTracker";
import Index from "./pages/Index";
import Eventos from "./pages/Eventos";
import EventDetail from "./pages/EventDetail";
import EventBooking from "./pages/EventBooking";
import Carrinho from "./pages/Carrinho";
const SignIn = safeLazy(() => import("./pages/SignIn"));
import Checkout from "./pages/Checkout";
import CheckoutPreview from "./pages/CheckoutPreview";
const EmailPreviewDebug = safeLazy(() => import('./pages/EmailPreviewDebug'));
import MeusIngressos from "./pages/MeusIngressos";
import TicketDetailPage from "./pages/TicketDetailPage";
import MeusPedidos from "./pages/MeusPedidos";

import ProducerLayout from "./pages/producer/ProducerLayout";
import ProducerDashboard from "./pages/producer/ProducerDashboard";
import ProducerEvents from "./pages/producer/ProducerEvents";
import ProducerEventForm from "./pages/producer/ProducerEventForm";
import ProducerEventPanel from "./pages/producer/ProducerEventPanel";
import ProducerSettings from "./pages/producer/ProducerSettings";
import ProducerInterestLists from "./pages/producer/ProducerInterestLists";
import ProducerInbox from "./pages/producer/ProducerInbox";
import ProducerFinancialPage from "./pages/producer/ProducerFinancial";

const Produtores = safeLazy(() => import("./pages/Produtores"));
const ProdutoresCorporativo = safeLazy(() => import("./pages/ProdutoresCorporativo"));
const ProdutoresFuncionalidades = safeLazy(() => import("./pages/ProdutoresFuncionalidades"));
const FuncionalidadeDetalhe = safeLazy(() => import("./pages/produtores/funcionalidades/FeatureDetail"));

const PublicCheckin = safeLazy(() => import("./pages/PublicCheckin"));
const OrganizerProfile = safeLazy(() => import("./pages/OrganizerProfile"));
const EmbedWidget = safeLazy(() => import("./pages/EmbedWidget"));
const Privacidade = safeLazy(() => import("./pages/Privacidade"));
const PoliticaPrivacidade = safeLazy(() => import("./pages/PoliticaPrivacidade"));
const TermosDeUso = safeLazy(() => import("./pages/TermosDeUso"));
const TermosCliente = safeLazy(() => import("./pages/TermosCliente"));
const TermosProdutor = safeLazy(() => import("./pages/TermosProdutor"));
const FilaVirtual = safeLazy(() => import("./pages/FilaVirtual"));
const AoVivoRedirectPage = safeLazy(() => import("./pages/AoVivoRedirectPage"));
const MeusCertificados = safeLazy(() => import("./pages/MeusCertificados"));
const CertificatePreviewDebug = safeLazy(() => import("./pages/CertificatePreviewDebug"));
// A.8 do plano de certificados: os 7 configs reais, renderizador de hoje contra
// o vetorial novo, com o diff. Só existe em DEV — a rota abaixo é condicional e
// o build de produção não inclui o chunk.
const CertificadosComparacao = safeLazy(() => import("./pages/dev/CertificadosComparacao"));
const AdminUserDetailDebug = safeLazy(() => import("./pages/debug/AdminUserDetailDebug"));
const SentryDebug = safeLazy(() => import("./pages/debug/SentryDebug"));
const VerificarCertificado = safeLazy(() => import("./pages/VerificarCertificado"));
const Blog = safeLazy(() => import("./pages/Blog"));
const BlogPost = safeLazy(() => import("./pages/BlogPost"));
const Changelog = safeLazy(() => import("./pages/Changelog"));
const NotificacoesConfig = safeLazy(() => import("./pages/NotificacoesConfig"));
const Cidades = safeLazy(() => import("./pages/Cidades"));
const Busca = safeLazy(() => import("./pages/Busca"));
const MeuPerfil = safeLazy(() => import("./pages/MeuPerfil"));
const EditarPerfil = safeLazy(() => import("./pages/EditarPerfil"));
const AlterarSenha = safeLazy(() => import("./pages/AlterarSenha"));
const ResetPassword = safeLazy(() => import("./pages/ResetPassword"));
const PerfilCidade = safeLazy(() => import("./pages/PerfilCidade"));
const MetodosPagamento = safeLazy(() => import("./pages/MetodosPagamento"));
const PerfilNotificacoes = safeLazy(() => import("./pages/PerfilNotificacoes"));
const PerfilSuporte = safeLazy(() => import("./pages/PerfilSuporte"));
const Favoritos = safeLazy(() => import("./pages/Favoritos"));
const MinhaCarteira = safeLazy(() => import("./pages/MinhaCarteira"));
const Revenda = safeLazy(() => import("./pages/Revenda"));
const RevendaCheckout = safeLazy(() => import("./pages/RevendaCheckout"));
const RevendaSucesso = safeLazy(() => import("./pages/RevendaSucesso"));
const PedidoRecuperacao = safeLazy(() => import("./pages/PedidoRecuperacao"));
const NotFound = safeLazy(() => import("./pages/NotFound"));
const ClaimGuestTicket = safeLazy(() => import("./pages/ClaimGuestTicket"));

// Global components — lazy loaded (import framer-motion)
// const SupportChat = safeLazy(() =>
//   import("@/components/SupportChat").then(m => ({ default: m.SupportChat }))
// );

// Producer pages — lazy loaded
const ProducerEventDashboardTab = safeLazy(() => import("./pages/producer/ProducerEventDashboardTab"));
const ProducerEventTicketsTab = safeLazy(() => import("./pages/producer/ProducerEventTicketsTab"));
const ProducerEventParticipants = safeLazy(() => import("./pages/producer/ProducerEventParticipants"));
const ProducerEventFinancial = safeLazy(() => import("./pages/producer/ProducerEventFinancial"));
const ProducerEventOrders = safeLazy(() => import("./pages/producer/ProducerEventOrders"));
const ProducerEventCheckin = safeLazy(() => import("./pages/producer/ProducerEventCheckin"));
const ProducerEventGuestlist = safeLazy(() => import("./pages/producer/ProducerEventGuestlist"));
const ProducerEventResales = safeLazy(() => import("./pages/producer/ProducerEventResales"));
const ProducerEventReports = safeLazy(() => import("./pages/producer/ProducerEventReports"));
const ProducerEventCoupons = safeLazy(() => import("./pages/producer/ProducerEventCoupons"));
const ProducerEventReviews = safeLazy(() => import("./pages/producer/ProducerEventReviews"));
const ScheduleActivitiesPage = safeLazy(() => import("./pages/producer/schedule/ScheduleActivitiesPage"));
const ScheduleActivityEditorPage = safeLazy(() => import("./pages/producer/schedule/ScheduleActivityEditorPage"));
const ScheduleOperationsPage = safeLazy(() => import("./pages/producer/schedule/ScheduleOperationsPage"));
const ScheduleSpeakersPage = safeLazy(() => import("./pages/producer/schedule/ScheduleSpeakersPage"));
const ScheduleSpeakerEditorPage = safeLazy(() => import("./pages/producer/schedule/ScheduleSpeakerEditorPage"));
const ScheduleSpacesPage = safeLazy(() => import("./pages/producer/schedule/ScheduleSpacesPage"));
const ScheduleMaterialsPage = safeLazy(() => import("./pages/producer/schedule/ScheduleMaterialsPage"));
const ScheduleQuestionsPage = safeLazy(() => import("./pages/producer/schedule/ScheduleQuestionsPage"));
const ScheduleSettingsPage = safeLazy(() => import("./pages/producer/schedule/ScheduleSettingsPage"));
const ScheduleReservationsPage = safeLazy(() => import("./pages/producer/schedule/ScheduleReservationsPage"));
const ScheduleRatingsPage = safeLazy(() => import("./pages/producer/schedule/ScheduleRatingsPage"));
const ScheduleHistoryPage = safeLazy(() => import("./pages/producer/schedule/ScheduleHistoryPage"));
const ProducerScheduleLayout = safeLazy(() => import("./pages/producer/schedule/ProducerScheduleLayout"));
const ScheduleStaffEventsPage = safeLazy(() => import("./pages/producer/schedule/ScheduleStaffEventsPage"));
const ScheduleStaffLayout = safeLazy(() => import("./pages/producer/schedule/ScheduleStaffLayout"));
const EventSchedulePage = safeLazy(() => import("./pages/schedule/EventSchedulePage"));
const ScheduleSessionPage = safeLazy(() => import("./pages/schedule/ScheduleSessionPage"));
const ScheduleSpeakerPage = safeLazy(() => import("./pages/schedule/ScheduleSpeakerPage"));
const ProducerOrganizationNew = safeLazy(() => import("./pages/producer/ProducerOrganizationNew"));
const ProducerOrganizations = safeLazy(() => import("./pages/producer/ProducerOrganizations"));
const ProducerOrganizationSettings = safeLazy(() => import("./pages/producer/ProducerOrganizationSettings"));
const ProducerOrganizationMembers = safeLazy(() => import("./pages/producer/ProducerOrganizationMembers"));
const ProducerEventPromoters = safeLazy(() => import("./pages/producer/ProducerEventPromoters"));
const ProducerEventMessages = safeLazy(() => import("./pages/producer/ProducerEventMessages"));
const ProducerInterestListForm = safeLazy(() => import("./pages/producer/ProducerInterestListForm"));
const InterestListPublic = safeLazy(() => import("./pages/InterestListPublic"));
const OrganizationInviteAccept = safeLazy(() => import("./pages/OrganizationInviteAccept"));
const AuthCallback = safeLazy(() => import("./pages/AuthCallback"));
const FAQ = safeLazy(() => import("./pages/FAQ"));

// Admin pages — lazy loaded
const AdminLayout = safeLazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = safeLazy(() => import("./pages/admin/AdminDashboard"));
const AdminEvents = safeLazy(() => import("./pages/admin/AdminEvents"));
const AdminEventAccess = safeLazy(() => import("./pages/admin/AdminEventAccess"));
const AdminUsers = safeLazy(() => import("./pages/admin/AdminUsers"));
const AdminProducers = safeLazy(() => import("./pages/admin/AdminProducers"));
const AdminProducerFiscal = safeLazy(() => import("./pages/admin/AdminProducerFiscal"));
const AdminProducerFiscalLegacy = safeLazy(() => import("./pages/admin/AdminProducerFiscal").then(module => ({ default: module.AdminProducerFiscalLegacy })));
const AdminFiscal = safeLazy(() => import("./pages/admin/AdminFiscal"));
const ProducerFiscal = safeLazy(() => import("./pages/producer/ProducerFiscal"));
const AdminProducerDetail = safeLazy(() => import("./pages/admin/AdminProducerDetail"));
const AdminOrders = safeLazy(() => import("./pages/admin/AdminOrders"));
const AdminReports = safeLazy(() => import("./pages/admin/AdminReports"));
const AdminFinance = safeLazy(() => import("./pages/admin/AdminFinance"));
const AdminCashForecast = safeLazy(() => import("./pages/admin/AdminCashForecast"));
const AdminWithdrawals = safeLazy(() => import("./pages/admin/AdminWithdrawals"));
const AdminSettings = safeLazy(() => import("./pages/admin/AdminSettings"));
const AdminOrganizations = safeLazy(() => import("./pages/admin/AdminOrganizations"));
const AdminOrgRoles = safeLazy(() => import("./pages/admin/AdminOrgRoles"));
const AdminEmailLogs = safeLazy(() => import("./pages/admin/AdminEmailLogs"));
const AdminMarketing = safeLazy(() => import("./pages/admin/AdminMarketing"));
const AdminMarketingCampaign = safeLazy(() => import("./pages/admin/AdminMarketingCampaign"));
const AdminLogs = safeLazy(() => import("./pages/admin/AdminLogs"));
const AdminCorporateLeads = safeLazy(() => import("./pages/admin/AdminCorporateLeads"));
const AdminAnticipations = safeLazy(() => import("./pages/admin/AdminAnticipations"));

// Staff pages — lazy loaded
const StaffEventList = safeLazy(() => import("./pages/staff/StaffEventList"));
const StaffCheckinScreen = safeLazy(() => import("./pages/staff/StaffCheckinScreen"));
const StaffJoin = safeLazy(() => import("./pages/staff/StaffJoin"));
const ProducerEventStaff = safeLazy(() => import("./pages/producer/ProducerEventStaff"));
const ProducerEventCertificates = safeLazy(() => import("./pages/producer/ProducerEventCertificates"));
const ProducerEventStreamingTab = safeLazy(() => import("./pages/producer/ProducerEventStreamingTab"));

const ProducerEventTeam = safeLazy(() => import("./pages/producer/ProducerEventTeam"));
const EventTeamInviteAccept = safeLazy(() => import("./pages/EventTeamInviteAccept"));
const EventFeedback = safeLazy(() => import("./pages/EventFeedback"));


// Redirects legado /producer/* -> /produtor/* que precisam interpolar params
// (Navigate `to` não substitui ":id" automaticamente — precisa ler via useParams)
const LegacyEventEditRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/produtor/eventos/${id}/editar`} replace />;
};

const LegacyEventPanelRedirect = () => {
  const params = useParams();
  const splat = params["*"];
  return <Navigate to={`/produtor/eventos/${params.id}/painel${splat ? `/${splat}` : ""}`} replace />;
};

const LazyFallback = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const App = () => {
  // SupportChat disabled on frontend for now (kept code in components/SupportChat.tsx)
  /*
  const [mountedSupportChat, setMountedSupportChat] = useState(false);

  useEffect(() => {
    const loadChat = () => {
      setMountedSupportChat(true);
      events.forEach(event => window.removeEventListener(event, loadChat));
    };

    const events = ["mousedown", "mousemove", "keydown", "scroll", "touchstart"];
    events.forEach(event => window.addEventListener(event, loadChat, { passive: true }));

    const backupTimer = setTimeout(loadChat, 15000);

    return () => {
      events.forEach(event => window.removeEventListener(event, loadChat));
      clearTimeout(backupTimer);
    };
  }, []);
  */

  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,   // 5 min — dados ficam "frescos" por 5 min
        gcTime: 15 * 60 * 1000,      // 15 min — cache mantido 15 min após desmontar
        refetchOnWindowFocus: false,  // não refaz fetch ao voltar à aba
        refetchOnMount: false,        // não refaz fetch se dados já em cache
        retry: 1,
      },
    },
  }));

  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <BrowserRouter>
        <ScrollToTop />
        <PageTracker />
        <AuthProvider>
        <OrganizationProvider>
          <CartProvider>
            <ErrorBoundary>
            <Suspense fallback={<LazyFallback />}>
            <Routes>
              {/* Pages with shared Navbar + Footer */}
              <Route element={<MainLayout />}>
                <Route path="/" element={<Index />} />
                <Route path="/produtores" element={<ProdutoresCorporativo />} />
                <Route path="/produtores/geral" element={<Produtores />} />
                <Route path="/produtores/corporativo" element={<Navigate to="/produtores" replace />} />
                <Route path="/produtores/funcionalidades" element={<ProdutoresFuncionalidades />} />
                <Route path="/produtores/funcionalidades/:slug" element={<FuncionalidadeDetalhe />} />
                <Route path="/eventos" element={<Eventos />} />
                <Route path="/produtor/:username/eventos/:slug" element={<EventDetail />} />
                <Route path="/produtor/:username/eventos/:slug/comprar" element={<EventBooking />} />
                <Route path="/organizacao/:username/eventos/:slug" element={<EventDetail />} />
                <Route path="/organizacao/:username/eventos/:slug/comprar" element={<EventBooking />} />
                <Route path="/eventos/:slug" element={<EventDetail />} />
                <Route path="/eventos/:slug/comprar" element={<EventBooking />} />
                <Route path="/eventos/:slug/programacao" element={<EventSchedulePage />} />
                <Route path="/eventos/:slug/programacao/s/:sessionId" element={<ScheduleSessionPage />} />
                <Route path="/eventos/:slug/programacao/palestrantes" element={<ScheduleSpeakerPage />} />
                <Route path="/eventos/:slug/programacao/palestrantes/:speakerId" element={<ScheduleSpeakerPage />} />
                <Route path="/produtor/:username/eventos/:slug/programacao" element={<EventSchedulePage />} />
                <Route path="/produtor/:username/eventos/:slug/programacao/s/:sessionId" element={<ScheduleSessionPage />} />
                <Route path="/produtor/:username/eventos/:slug/programacao/palestrantes" element={<ScheduleSpeakerPage />} />
                <Route path="/produtor/:username/eventos/:slug/programacao/palestrantes/:speakerId" element={<ScheduleSpeakerPage />} />
                <Route path="/organizacao/:username/eventos/:slug/programacao" element={<EventSchedulePage />} />
                <Route path="/organizacao/:username/eventos/:slug/programacao/s/:sessionId" element={<ScheduleSessionPage />} />
                <Route path="/organizacao/:username/eventos/:slug/programacao/palestrantes" element={<ScheduleSpeakerPage />} />
                <Route path="/organizacao/:username/eventos/:slug/programacao/palestrantes/:speakerId" element={<ScheduleSpeakerPage />} />
              <Route path="/carrinho" element={<Carrinho />} />
                <Route path="/resgatar-cortesia/:accessCode" element={<ClaimGuestTicket />} />
                <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
                <Route path="/pedido/:orderId" element={<ProtectedRoute><PedidoRecuperacao /></ProtectedRoute>} />
                <Route path="/meus-ingressos" element={<ProtectedRoute><MeusIngressos /></ProtectedRoute>} />
                <Route path="/meus-ingressos/:ticketId" element={<ProtectedRoute><TicketDetailPage /></ProtectedRoute>} />
                <Route path="/meus-pedidos" element={<ProtectedRoute><MeusPedidos /></ProtectedRoute>} />
                <Route path="/produtor/:username" element={<OrganizerProfile />} />
                <Route path="/organizacao/:slug" element={<OrganizerProfile />} />
                <Route path="/minha-conta/privacidade" element={<ProtectedRoute><Privacidade /></ProtectedRoute>} />
                <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
                <Route path="/termos-de-uso" element={<TermosDeUso />} />
                <Route path="/termos-de-uso/cliente" element={<TermosCliente />} />
                <Route path="/termos-de-uso/produtor" element={<TermosProdutor />} />
                <Route path="/termos" element={<Navigate to="/termos-de-uso/cliente" replace />} />
                <Route path="/meus-certificados" element={<ProtectedRoute><MeusCertificados /></ProtectedRoute>} />
                <Route path="/verificar-certificado" element={<VerificarCertificado />} />
                <Route path="/fila/:slug" element={<FilaVirtual />} />
                <Route path="/ao-vivo/:token" element={<AoVivoRedirectPage />} />
                <Route path="/changelog" element={<Changelog />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/cidades" element={<Cidades />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/busca" element={<Busca />} />
                <Route path="/notificacoes" element={<NotificacoesConfig />} />
                <Route path="/meu-perfil" element={<ProtectedRoute><MeuPerfil /></ProtectedRoute>} />
                <Route path="/meu-perfil/editar" element={<ProtectedRoute><EditarPerfil /></ProtectedRoute>} />
                <Route path="/meu-perfil/alterar-senha" element={<ProtectedRoute><AlterarSenha /></ProtectedRoute>} />
                <Route path="/meu-perfil/cidade" element={<ProtectedRoute><PerfilCidade /></ProtectedRoute>} />
                <Route path="/meu-perfil/pagamento" element={<ProtectedRoute><MetodosPagamento /></ProtectedRoute>} />
                <Route path="/meu-perfil/notificacoes" element={<ProtectedRoute><PerfilNotificacoes /></ProtectedRoute>} />
                <Route path="/meu-perfil/suporte" element={<ProtectedRoute><PerfilSuporte /></ProtectedRoute>} />
                <Route path="/favoritos" element={<ProtectedRoute><Favoritos /></ProtectedRoute>} />
                <Route path="/minha-carteira" element={<ProtectedRoute><MinhaCarteira /></ProtectedRoute>} />
                <Route path="/revenda" element={<Revenda />} />
                <Route path="/revenda/:listingId" element={<RevendaCheckout />} />
                <Route path="/revenda/:listingId/sucesso" element={<ProtectedRoute><RevendaSucesso /></ProtectedRoute>} />
                <Route path="*" element={<NotFound />} />
              </Route>

              {/* Standalone pages (no shared Navbar/Footer) */}
              {import.meta.env.DEV ? (
                <>
                  <Route path="/debug/certificate-preview" element={<CertificatePreviewDebug />} />
                  <Route path="/debug/email-preview" element={<EmailPreviewDebug />} />
                  <Route path="/debug/admin-user-detail" element={<AdminUserDetailDebug />} />
                  <Route path="/debug/certificados-comparacao" element={<CertificadosComparacao />} />
                </>
              ) : null}
              <Route path="/debug/sentry" element={<SentryDebug />} />
              <Route path="/checkin/:accessCode" element={<PublicCheckin />} />
              <Route path="/recuperar-senha" element={<ResetPassword />} />
              <Route path="/embed" element={<EmbedWidget />} />
              <Route path="/lista/:slug" element={<InterestListPublic />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/entrar" element={<SignIn />} />
              <Route path="/convite-org" element={<OrganizationInviteAccept />} />

              <Route path="/checkout-preview" element={<CheckoutPreview />} />

              {/* Staff Portal (standalone, no MainLayout) */}
              <Route path="/staff" element={<ProtectedRoute allowedRoles={["staff", "admin", "producer"]}><StaffEventList /></ProtectedRoute>} />
              <Route path="/staff/checkin/:eventId" element={<ProtectedRoute allowedRoles={["staff", "admin", "producer"]}><StaffCheckinScreen /></ProtectedRoute>} />
              <Route path="/staff/join/:code" element={<StaffJoin />} />
              <Route path="/staff/programacao" element={<ProtectedRoute allowedRoles={["staff","producer","admin","buyer"]}><ScheduleStaffEventsPage /></ProtectedRoute>} />
              <Route path="/staff/eventos/:id/programacao" element={<ProtectedRoute allowedRoles={["staff","producer","admin","buyer"]}><ScheduleStaffLayout /></ProtectedRoute>}>
                <Route index element={<Navigate to="operacao" replace />} />
                <Route path="atividades" element={<ScheduleActivitiesPage />} />
                <Route path="operacao" element={<ScheduleOperationsPage />} />
                <Route path="perguntas" element={<ScheduleQuestionsPage />} />
                <Route path="reservas/:sessionId" element={<ScheduleReservationsPage />} />
              </Route>
              <Route path="/invite/event-team/:token" element={<EventTeamInviteAccept />} />
              <Route path="/avaliar/:token" element={<EventFeedback />} />

              {/* Producer Panel (Português /produtor) */}
              <Route
                path="/produtor"
                element={
                  <ProtectedRoute allowedRoles={["producer", "admin"]}>
                    <ProducerLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/produtor/dashboard" replace />} />
                <Route path="dashboard" element={<ProducerDashboard />} />
                {/* Eventos (Português) */}
                <Route path="eventos" element={<ProducerEvents />} />
                <Route path="eventos/novo" element={<ProducerEventForm returnPath="/produtor/eventos" />} />
                <Route path="eventos/:id/editar" element={<ProducerEventForm returnPath="/produtor/eventos" />} />
                <Route path="eventos/:id/relatorios" element={<ProducerEventReports />} />
                <Route path="eventos/:id/painel" element={<ProducerEventPanel eventsBasePath="/produtor/eventos" listPath="/produtor/eventos" panelSlug="painel" />}>
                  <Route index element={<ProducerEventDashboardTab />} />
                  <Route path="ingressos" element={<ProducerEventTicketsTab />} />
                  <Route path="pedidos" element={<ProducerEventOrders />} />
                  <Route path="participantes" element={<ProducerEventParticipants />} />
                  <Route path="lista-convidados" element={<ProducerEventGuestlist />} />
                  <Route path="checkin" element={<ProducerEventCheckin />} />
                  <Route path="financeiro" element={<ProducerEventFinancial />} />
                  <Route path="mensagens" element={<ProducerEventMessages />} />
                  <Route path="cupons" element={<ProducerEventCoupons />} />
                  <Route path="promotores" element={<ProducerEventPromoters />} />
                  <Route path="equipe" element={<ProducerEventTeam />} />
                  <Route path="certificados" element={<ProducerEventCertificates />} />
                  <Route path="transmissao" element={<ProducerEventStreamingTab />} />
                  <Route path="revenda" element={<ProducerEventResales />} />
                  <Route path="avaliacoes" element={<ProducerEventReviews />} />
                  <Route path="programacao" element={<ProducerScheduleLayout />}>
                    <Route index element={<Navigate to="atividades" replace />} />
                    <Route path="atividades" element={<ScheduleActivitiesPage />} />
                    <Route path="nova" element={<ScheduleActivityEditorPage />} />
                    <Route path="editar/:sessionId" element={<ScheduleActivityEditorPage />} />
                    <Route path="operacao" element={<ScheduleOperationsPage />} />
                    <Route path="palestrantes" element={<ScheduleSpeakersPage />} />
                    <Route path="palestrantes/:speakerId" element={<ScheduleSpeakerEditorPage />} />
                    <Route path="espacos" element={<ScheduleSpacesPage />} />
                    <Route path="materiais" element={<ScheduleMaterialsPage />} />
                    <Route path="perguntas" element={<ScheduleQuestionsPage />} />
                    <Route path="config" element={<ScheduleSettingsPage />} />
                    <Route path="importar" element={<Navigate to="../atividades" replace />} />
                    <Route path="reservas/:sessionId" element={<ScheduleReservationsPage />} />
                    <Route path="avaliacoes/:sessionId" element={<ScheduleRatingsPage />} />
                    <Route path="historico" element={<ScheduleHistoryPage />} />
                  </Route>
                  {/* English subroute aliases inside painel */}
                  <Route path="tickets" element={<ProducerEventTicketsTab />} />
                  <Route path="orders" element={<ProducerEventOrders />} />
                  <Route path="participants" element={<ProducerEventParticipants />} />
                  <Route path="guestlist" element={<ProducerEventGuestlist />} />
                  <Route path="financial" element={<ProducerEventFinancial />} />
                  <Route path="messages" element={<ProducerEventMessages />} />
                  <Route path="coupons" element={<ProducerEventCoupons />} />
                  <Route path="promoters" element={<ProducerEventPromoters />} />
                  <Route path="team" element={<ProducerEventTeam />} />
                  <Route path="certificates" element={<ProducerEventCertificates />} />
                  <Route path="streaming" element={<ProducerEventStreamingTab />} />
                  <Route path="resales" element={<ProducerEventResales />} />
                  <Route path="reviews" element={<ProducerEventReviews />} />
                  <Route path="schedule" element={<ProducerScheduleLayout />}>
                    <Route index element={<Navigate to="atividades" replace />} />
                    <Route path="atividades" element={<ScheduleActivitiesPage />} />
                    <Route path="nova" element={<ScheduleActivityEditorPage />} />
                    <Route path="editar/:sessionId" element={<ScheduleActivityEditorPage />} />
                    <Route path="operacao" element={<ScheduleOperationsPage />} />
                    <Route path="palestrantes" element={<ScheduleSpeakersPage />} />
                    <Route path="palestrantes/:speakerId" element={<ScheduleSpeakerEditorPage />} />
                    <Route path="espacos" element={<ScheduleSpacesPage />} />
                    <Route path="materiais" element={<ScheduleMaterialsPage />} />
                    <Route path="perguntas" element={<ScheduleQuestionsPage />} />
                    <Route path="config" element={<ScheduleSettingsPage />} />
                    <Route path="importar" element={<Navigate to="../atividades" replace />} />
                    <Route path="reservas/:sessionId" element={<ScheduleReservationsPage />} />
                    <Route path="avaliacoes/:sessionId" element={<ScheduleRatingsPage />} />
                    <Route path="historico" element={<ScheduleHistoryPage />} />
                  </Route>
                </Route>

                {/* Aliases under /produtor for English paths */}
                <Route path="events" element={<ProducerEvents />} />
                <Route path="events/new" element={<ProducerEventForm returnPath="/produtor/eventos" />} />
                <Route path="events/:id/edit" element={<ProducerEventForm returnPath="/produtor/eventos" />} />
                <Route path="events/:id/reports" element={<ProducerEventReports />} />
                <Route path="events/:id/panel" element={<ProducerEventPanel eventsBasePath="/produtor/eventos" listPath="/produtor/eventos" panelSlug="painel" />}>
                  <Route index element={<ProducerEventDashboardTab />} />
                  <Route path="tickets" element={<ProducerEventTicketsTab />} />
                  <Route path="orders" element={<ProducerEventOrders />} />
                  <Route path="participants" element={<ProducerEventParticipants />} />
                  <Route path="guestlist" element={<ProducerEventGuestlist />} />
                  <Route path="checkin" element={<ProducerEventCheckin />} />
                  <Route path="financial" element={<ProducerEventFinancial />} />
                  <Route path="messages" element={<ProducerEventMessages />} />
                  <Route path="coupons" element={<ProducerEventCoupons />} />
                  <Route path="promoters" element={<ProducerEventPromoters />} />
                  <Route path="team" element={<ProducerEventTeam />} />
                  <Route path="certificates" element={<ProducerEventCertificates />} />
                  <Route path="streaming" element={<ProducerEventStreamingTab />} />
                  <Route path="resales" element={<ProducerEventResales />} />
                  <Route path="reviews" element={<ProducerEventReviews />} />
                  <Route path="ingressos" element={<ProducerEventTicketsTab />} />
                  <Route path="pedidos" element={<ProducerEventOrders />} />
                  <Route path="participantes" element={<ProducerEventParticipants />} />
                  <Route path="lista-convidados" element={<ProducerEventGuestlist />} />
                  <Route path="financeiro" element={<ProducerEventFinancial />} />
                  <Route path="mensagens" element={<ProducerEventMessages />} />
                  <Route path="promotores" element={<ProducerEventPromoters />} />
                  <Route path="equipe" element={<ProducerEventTeam />} />
                  <Route path="certificados" element={<ProducerEventCertificates />} />
                  <Route path="transmissao" element={<ProducerEventStreamingTab />} />
                  <Route path="revenda" element={<ProducerEventResales />} />
                  <Route path="avaliacoes" element={<ProducerEventReviews />} />
                  <Route path="programacao" element={<ProducerScheduleLayout />}>
                    <Route index element={<Navigate to="atividades" replace />} />
                    <Route path="atividades" element={<ScheduleActivitiesPage />} />
                    <Route path="nova" element={<ScheduleActivityEditorPage />} />
                    <Route path="editar/:sessionId" element={<ScheduleActivityEditorPage />} />
                    <Route path="operacao" element={<ScheduleOperationsPage />} />
                    <Route path="palestrantes" element={<ScheduleSpeakersPage />} />
                    <Route path="palestrantes/:speakerId" element={<ScheduleSpeakerEditorPage />} />
                    <Route path="espacos" element={<ScheduleSpacesPage />} />
                    <Route path="materiais" element={<ScheduleMaterialsPage />} />
                    <Route path="perguntas" element={<ScheduleQuestionsPage />} />
                    <Route path="config" element={<ScheduleSettingsPage />} />
                    <Route path="importar" element={<Navigate to="../atividades" replace />} />
                    <Route path="reservas/:sessionId" element={<ScheduleReservationsPage />} />
                    <Route path="avaliacoes/:sessionId" element={<ScheduleRatingsPage />} />
                    <Route path="historico" element={<ScheduleHistoryPage />} />
                  </Route>
                  <Route path="schedule" element={<ProducerScheduleLayout />}>
                    <Route index element={<Navigate to="atividades" replace />} />
                    <Route path="atividades" element={<ScheduleActivitiesPage />} />
                    <Route path="nova" element={<ScheduleActivityEditorPage />} />
                    <Route path="editar/:sessionId" element={<ScheduleActivityEditorPage />} />
                    <Route path="operacao" element={<ScheduleOperationsPage />} />
                    <Route path="palestrantes" element={<ScheduleSpeakersPage />} />
                    <Route path="palestrantes/:speakerId" element={<ScheduleSpeakerEditorPage />} />
                    <Route path="espacos" element={<ScheduleSpacesPage />} />
                    <Route path="materiais" element={<ScheduleMaterialsPage />} />
                    <Route path="perguntas" element={<ScheduleQuestionsPage />} />
                    <Route path="config" element={<ScheduleSettingsPage />} />
                    <Route path="importar" element={<Navigate to="../atividades" replace />} />
                    <Route path="reservas/:sessionId" element={<ScheduleReservationsPage />} />
                    <Route path="avaliacoes/:sessionId" element={<ScheduleRatingsPage />} />
                    <Route path="historico" element={<ScheduleHistoryPage />} />
                  </Route>
                </Route>

                <Route path="inbox" element={<ProducerInbox />} />
                <Route path="lista-de-interesse" element={<ProducerInterestLists />} />
                <Route path="lista-de-interesse/nova" element={<ProducerInterestListForm />} />
                <Route path="lista-de-interesse/:id/editar" element={<ProducerInterestListForm />} />
                <Route path="interest-lists" element={<ProducerInterestLists />} />
                <Route path="interest-lists/new" element={<ProducerInterestListForm />} />
                <Route path="interest-lists/:id/edit" element={<ProducerInterestListForm />} />
                <Route path="financeiro" element={<ProducerFinancialPage />} />
                <Route path="financial" element={<ProducerFinancialPage />} />
                <Route path="configuracoes" element={<ProducerSettings />} />
                <Route path="notas-fiscais" element={<ProducerFiscal />} />
                <Route path="settings" element={<ProducerSettings />} />
                <Route path="organizacoes" element={<ProducerOrganizations />} />
                <Route path="organizacoes/nova" element={<ProducerOrganizationNew />} />
                <Route path="organizacoes/:id/configuracoes" element={<ProducerOrganizationSettings />} />
                <Route path="organizacoes/:id/membros" element={<ProducerOrganizationMembers />} />
              </Route>

              {/* Legacy /producer Panel Alias - redirects to /produtor equivalent */}
              <Route path="/producer/dashboard" element={<Navigate to="/produtor/dashboard" replace />} />
              <Route path="/producer/events" element={<Navigate to="/produtor/eventos" replace />} />
              <Route path="/producer/events/new" element={<Navigate to="/produtor/eventos/novo" replace />} />
              <Route path="/producer/events/:id/edit" element={<LegacyEventEditRedirect />} />
              <Route path="/producer/events/:id/panel/*" element={<LegacyEventPanelRedirect />} />
              <Route path="/producer/financial" element={<Navigate to="/produtor/financeiro" replace />} />
              <Route path="/producer/settings" element={<Navigate to="/produtor/configuracoes" replace />} />
              <Route path="/producer/organizacoes" element={<Navigate to="/produtor/organizacoes" replace />} />
              <Route path="/producer" element={<Navigate to="/produtor/dashboard" replace />} />

              {/* Admin Panel */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/admin/relatorios" replace />} />
                <Route path="relatorios" element={<AdminDashboard />} />
                <Route path="eventos" element={<AdminEvents />} />
                <Route path="eventos/novo" element={<ProducerEventForm returnPath="/admin/eventos" />} />
                <Route path="eventos/:id/editar" element={<ProducerEventForm returnPath="/admin/eventos" />} />
                <Route path="eventos/:id/acessos" element={<AdminEventAccess />} />
                <Route path="eventos/:id/painel" element={<ProducerEventPanel eventsBasePath="/admin/eventos" listPath="/admin/eventos" panelSlug="painel" />}>
                  <Route index element={<ProducerEventDashboardTab />} />
                  <Route path="ingressos" element={<ProducerEventTicketsTab />} />
                  <Route path="pedidos" element={<ProducerEventOrders />} />
                  <Route path="participantes" element={<ProducerEventParticipants />} />
                  <Route path="lista-convidados" element={<ProducerEventGuestlist />} />
                  <Route path="checkin" element={<ProducerEventCheckin />} />
                  <Route path="financeiro" element={<ProducerEventFinancial />} />
                  <Route path="mensagens" element={<ProducerEventMessages />} />
                  <Route path="cupons" element={<ProducerEventCoupons />} />
                  <Route path="promotores" element={<ProducerEventPromoters />} />
                  <Route path="equipe-evento" element={<ProducerEventStaff />} />
                  <Route path="equipe" element={<ProducerEventTeam />} />
                  <Route path="certificados" element={<ProducerEventCertificates />} />
                  <Route path="transmissao" element={<ProducerEventStreamingTab />} />
                  <Route path="revenda" element={<ProducerEventResales />} />
                  <Route path="avaliacoes" element={<ProducerEventReviews />} />
                  <Route path="programacao" element={<ProducerScheduleLayout />}>
                    <Route index element={<Navigate to="atividades" replace />} />
                    <Route path="atividades" element={<ScheduleActivitiesPage />} />
                    <Route path="nova" element={<ScheduleActivityEditorPage />} />
                    <Route path="editar/:sessionId" element={<ScheduleActivityEditorPage />} />
                    <Route path="operacao" element={<ScheduleOperationsPage />} />
                    <Route path="palestrantes" element={<ScheduleSpeakersPage />} />
                    <Route path="palestrantes/:speakerId" element={<ScheduleSpeakerEditorPage />} />
                    <Route path="espacos" element={<ScheduleSpacesPage />} />
                    <Route path="materiais" element={<ScheduleMaterialsPage />} />
                    <Route path="perguntas" element={<ScheduleQuestionsPage />} />
                    <Route path="config" element={<ScheduleSettingsPage />} />
                    <Route path="importar" element={<Navigate to="../atividades" replace />} />
                    <Route path="reservas/:sessionId" element={<ScheduleReservationsPage />} />
                    <Route path="avaliacoes/:sessionId" element={<ScheduleRatingsPage />} />
                    <Route path="historico" element={<ScheduleHistoryPage />} />
                  </Route>
                  <Route path="schedule" element={<ProducerScheduleLayout />}>
                    <Route index element={<Navigate to="atividades" replace />} />
                    <Route path="atividades" element={<ScheduleActivitiesPage />} />
                    <Route path="nova" element={<ScheduleActivityEditorPage />} />
                    <Route path="editar/:sessionId" element={<ScheduleActivityEditorPage />} />
                    <Route path="operacao" element={<ScheduleOperationsPage />} />
                    <Route path="palestrantes" element={<ScheduleSpeakersPage />} />
                    <Route path="palestrantes/:speakerId" element={<ScheduleSpeakerEditorPage />} />
                    <Route path="espacos" element={<ScheduleSpacesPage />} />
                    <Route path="materiais" element={<ScheduleMaterialsPage />} />
                    <Route path="perguntas" element={<ScheduleQuestionsPage />} />
                    <Route path="config" element={<ScheduleSettingsPage />} />
                    <Route path="importar" element={<Navigate to="../atividades" replace />} />
                    <Route path="reservas/:sessionId" element={<ScheduleReservationsPage />} />
                    <Route path="avaliacoes/:sessionId" element={<ScheduleRatingsPage />} />
                    <Route path="historico" element={<ScheduleHistoryPage />} />
                  </Route>
                </Route>
                <Route path="usuarios" element={<AdminUsers />} />
                <Route path="produtores" element={<AdminProducers />} />
                <Route path="produtores/:producerId/notas-fiscais" element={<AdminProducerFiscal />} />
                <Route path="produtores/:producerId/notas-fiscais/asaas" element={<AdminProducerFiscalLegacy />} />
                <Route path="notas-fiscais" element={<AdminFiscal />} />
                <Route path="produtores/:producerId" element={<AdminProducerDetail />} />
                <Route path="pedidos" element={<AdminOrders />} />
                <Route path="relatorios-detalhados" element={<AdminReports />} />
                <Route path="financeiro" element={<AdminFinance />} />
                <Route path="previsao-entradas" element={<AdminCashForecast />} />
                <Route path="antecipacoes" element={<AdminAnticipations />} />
                <Route path="saques" element={<AdminWithdrawals />} />
                <Route path="organizacoes" element={<AdminOrganizations />} />
                <Route path="organizacoes/cargos" element={<AdminOrgRoles />} />
                <Route path="leads-corporativos" element={<AdminCorporateLeads />} />
                <Route path="emails" element={<AdminEmailLogs />} />
                <Route path="marketing" element={<AdminMarketing />} />
                <Route path="marketing/:campaignId" element={<AdminMarketingCampaign />} />
                <Route path="logs" element={<AdminLogs />} />
                <Route path="configuracoes" element={<AdminSettings />} />
              </Route>

            </Routes>
            </Suspense>
            </ErrorBoundary>
            <MobileBottomNav />
            {/* SupportChat disabled on frontend
            <Suspense fallback={null}>
              {mountedSupportChat && <SupportChat />}
            </Suspense>
            */}
          </CartProvider>
        </OrganizationProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  );
};

export default App;



